// Shift-end notes review notifications for Supervisors (architecture sprint-15 §9.2; not for Admins). Mounted in
// AppShell only when me.access.notesReviewNotify. One OS notification per review item: the seen set lives in
// localStorage['tt-notes-review-seen'] (≤ 200 ids, FIFO) and the tag is the event id, so tabs collapse to one.
// A note added to a closed shift later is a new event id (A-6), so it notifies again. Clicking opens the Notes
// page filtered to that shift.
import type { NotesReviewItemDto } from '@shared/note-rules';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '@/hooks/use-me';
import { useNotesReview } from '@/hooks/use-notes-review';
import { addSeenNoteReviews, readSeenNoteReviews } from '@/lib/alarm-store';
import { showReviewNotification } from '@/lib/notifications';
import { clockIn, dateIn } from '@/lib/shift-format';

export const NOTES_TO_REVIEW_TITLE = 'Notes to review';
const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' });

/** "Notes from Mia's shift (22:00–Sat 06:00), 2 to review": the end carries its weekday when the day differs. */
export function reviewNotificationBody(item: NotesReviewItemDto, tz: string): string {
  const start = clockIn(item.shift.startedAt, tz);
  const sameDay = dateIn(item.shift.startedAt, tz) === dateIn(item.shift.endedAt, tz);
  const endDay = WEEKDAY.format(new Date(`${dateIn(item.shift.endedAt, tz)}T12:00:00Z`));
  const end = sameDay ? clockIn(item.shift.endedAt, tz) : `${endDay} ${clockIn(item.shift.endedAt, tz)}`;
  return `Notes from ${item.member.name}'s shift (${start}–${end}), ${item.pendingCount} to review`;
}

export function reviewPath(item: Pick<NotesReviewItemDto, 'projectId' | 'shift'>): string {
  return `/oversight/notes?project=${item.projectId}&shift=${item.shift.id}`;
}

export function NotesReviewNotifier(): null {
  const { data } = useNotesReview();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const tz = me?.timezone ?? 'UTC';
  useEffect(() => {
    if (!data?.notify) return;
    const seen = new Set(readSeenNoteReviews());
    const fresh: string[] = [];
    for (const item of data.items) {
      if (seen.has(item.eventId)) continue;
      showReviewNotification(item.eventId, NOTES_TO_REVIEW_TITLE, reviewNotificationBody(item, tz), () => navigate(reviewPath(item)));
      fresh.push(item.eventId);
    }
    if (fresh.length > 0) addSeenNoteReviews(fresh);
  }, [data, tz, navigate]);
  return null;
}
